import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api.ts";
import { money } from "../format.ts";
import { Act } from "../ui.tsx";

interface Row { id: string; title: { fr: string }; cityKey: string; status: string }
interface Quote { bookable: boolean; problems: string[]; currency: string; breakdown: { nights: number; totalMinor: number; depositMinor: number } | null }
const PROBLEM: Record<string, string> = { dates_taken: "Ces dates sont déjà prises.", past_date: "Date passée.", too_many_guests: "Trop de voyageurs pour ce bien.", min_nights: "Séjour trop court.", max_nights: "Séjour trop long.", listing_unavailable: "Bien indisponible.", own_listing: "Bien du client." };

/** A stay taken by phone or WhatsApp. It enters the same flow as one the guest requested online. */
export function NewBooking() {
  const nav = useNavigate();
  const [listings, setListings] = useState<Row[]>([]);
  const [f, setF] = useState({ listingId: "", checkIn: "", checkOut: "", guests: 2, name: "", phone: "", message: "" });
  const [q, setQ] = useState<Quote | null>(null);
  useEffect(() => { api<Row[]>("/v1/staff/listings?status=published").then(setListings).catch(() => undefined); }, []);

  const datesOk = f.listingId && f.checkIn && f.checkOut && f.checkOut > f.checkIn;
  useEffect(() => {
    setQ(null);
    if (!datesOk) return;
    const t = setTimeout(() => { api<Quote>("/v1/quote", { body: { listingId: f.listingId, checkIn: f.checkIn, checkOut: f.checkOut, guests: f.guests } }).then(setQ).catch(() => setQ(null)); }, 250);
    return () => clearTimeout(t);
  }, [f.listingId, f.checkIn, f.checkOut, f.guests, datesOk]);

  const phone = f.phone.replace(/\D/g, "");
  const valid = datesOk && f.name.trim().length >= 2 && phone.length >= 8 && q?.bookable;

  return (
    <section>
      <header className="pagehead"><h1>Nouvelle réservation</h1><p className="muted small">Pour un client qui réserve par téléphone ou WhatsApp. La demande suit ensuite le circuit normal : confirmation auprès du propriétaire, puis paiement.</p></header>
      <div className="card stackc">
        <label className="field"><span>Bien</span><select value={f.listingId} onChange={(e) => setF({ ...f, listingId: e.target.value })}><option value="">—</option>{listings.map((l) => <option key={l.id} value={l.id}>{l.title.fr} · {l.cityKey}</option>)}</select></label>
        <div className="formrow">
          <label className="field"><span>Arrivée</span><input type="date" value={f.checkIn} onChange={(e) => setF({ ...f, checkIn: e.target.value })} /></label>
          <label className="field"><span>Départ</span><input type="date" value={f.checkOut} min={f.checkIn} onChange={(e) => setF({ ...f, checkOut: e.target.value })} /></label>
          <label className="field"><span>Voyageurs</span><input type="number" min={1} max={30} value={f.guests} onChange={(e) => setF({ ...f, guests: Math.max(1, Number(e.target.value) || 1) })} /></label>
        </div>
        <div className="formrow">
          <label className="field"><span>Nom du client</span><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
          <label className="field"><span>Téléphone (avec indicatif)</span><input inputMode="numeric" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="22236123456" /></label>
        </div>
        <label className="field"><span>Note</span><input value={f.message} maxLength={1000} onChange={(e) => setF({ ...f, message: e.target.value })} /></label>

        {datesOk && q && (q.bookable && q.breakdown
          ? <p className="card big num">{q.breakdown.nights} nuit{q.breakdown.nights > 1 ? "s" : ""} · {money(q.breakdown.totalMinor, q.currency)}</p>
          : <p className="err" role="alert">{q.problems.map((p) => PROBLEM[p] ?? p).join(" ")}</p>)}

        <div className="quick"><Act disabled={!valid} run={async () => {
          const r = await api<{ booking: { id: string } }>("/v1/staff/bookings", { body: { listingId: f.listingId, checkIn: f.checkIn, checkOut: f.checkOut, guests: f.guests, ...(f.message.trim() ? { message: f.message.trim() } : {}), guest: { name: f.name.trim(), phone } } });
          nav(`/b/${r.booking.id}`);
        }}>Créer la demande</Act></div>
      </div>
    </section>
  );
}
